import { GradientButton } from "../GradientButton";
import { useStateWithSearchParams, useAppDispatch } from "../../../hooks";
import {
  accountsApi,
  campaignsApi,
  useAnalyseAllMutation,
  useAnalyseMutation,
  useGetCampaignSummaryQuery,
} from "../../../api";
import { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { setIsNotificationsOpen } from "features/companySlice";
import { QUERY_SEPARATOR } from "../../../constant";

type SearchParams = {
  selected?: string;
};

export const AnalyzeAllBtn = ({
  campaign_id,
  fullWidth,
}: {
  campaign_id: string;
  fullWidth?: boolean;
}) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [analyse] = useAnalyseMutation();
  const [analyseAll, { isLoading: isAnalysingAll }] = useAnalyseAllMutation();
  const { data: summary } = useGetCampaignSummaryQuery({
    campaign_id,
  });

  useEffect(() => {
    if (isAnalysingAll) {
      setIsLoading(true);
    }
  }, [isAnalysingAll]);

  useEffect(() => {
    setIsLoading(summary?.analyze_all_status === "in_progress");
  }, [summary]);

  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      selected: undefined,
    },
  );

  const analyseHandler = useCallback(() => {
    const handler = () => {
      setSearchValue("selected", undefined);
      dispatch(campaignsApi.util.invalidateTags(["activities", "summary"]));
      dispatch(accountsApi.util.invalidateTags(["list-view"]));
      dispatch(setIsNotificationsOpen(true));
    };

    if (searchDict.selected) {
      analyse({
        account_ids: searchDict.selected.split(QUERY_SEPARATOR),
        campaign_id,
      })
        .unwrap()
        .then(() => handler())
        .catch((err) => {
          toast.error(
            <CustomToast
              type="error"
              title="Something went wrong during the analysis."
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
            />,
          );
        });
    } else {
      analyseAll({ campaign_id })
        .unwrap()
        .then(() => handler())
        .catch((err) => {
          toast.error(
            <CustomToast
              type="error"
              title="Something went wrong during the analysis."
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
            />,
          );
          setIsLoading(false);
        });
    }
  }, [searchDict.selected, campaign_id]);

  return (
    <GradientButton
      fullWidth={fullWidth}
      onClick={analyseHandler}
      disabled={"not_allowed" === summary?.analyze_all_status || isLoading}
      startIcon={
        isLoading && <CircularProgress size={24} sx={{ color: "inherit" }} />
      }
    >
      {isLoading
        ? "Analysis in Progress"
        : searchDict.selected
          ? "Analyze"
          : "Analyze Accounts"}
    </GradientButton>
  );
};
